import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Spinner, Typography } from "@material-tailwind/react";
import { ShoppingCartIcon } from "@heroicons/react/20/solid";
import { Link, useNavigate } from "react-router-dom";

import { searchOutletController } from "../../Controllers/SearchOutletController";
import { issueDetailsController } from "../../Controllers/IssueDetailsController";
import { alertController } from "../../../../Common/Controllers/AlertController";
import { loginSignupController } from "../../../../Common/Components/Standalone/LoginOrSignup/LoginSignupController";
import { storageController } from "../../../../Common/Controllers/StoreControllerFactory";
import { userAccountController } from "../../../../Common/Controllers/UserAccountController";
import { MAX_ISSUES_AVAILABLE } from "../../../../Common/DTOs/Stock";
import { StockistIssue } from "../../../../Common/DTOs/StockistIssue";
import { Dialog } from "../Dialog";

export const SearchOutlet = observer(() => {
  const {
    isSearching,
    isLoadingMore,
    upcomingResults,
    latestResults,
    extrasResults,
    hasMoreLatest,
  } = searchOutletController;

  return (
    <div className="mt-6 xsm:px-4 pb-2 text-white min-h-[60vh]">
      <>
        <section className="pb-2">
          <Typography variant="h2" className="text-white text-2xl md:text-3xl mb-2 animate-fadeUp">
            Search Results
          </Typography>

          <div className="lg:px-2 w-full grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] justify-items-center gap-x-1 gap-y-2 py-2 mt-2">
            {searchOutletController.mergedUpcomingLatest.length > 0 ? (
              searchOutletController.mergedUpcomingLatest.map((issue) =>
                searchOutletController.upcomingResults.includes(issue) ? (
                  <MinifiedUpcomingIssueCard issue={issue} key={issue.id} />
                ) : (
                  <MinifiedIssueCard issue={issue} key={issue.id} />
                )
              )
            ) : !searchOutletController.isDebouncing && isSearching ? (
              Array.from({ length: 12 }).map((_, i) => (
                <div className="animate-fadeIn" key={`skel-${i}`}>
                  <MinifiedIssueSkeleton />
                </div>
              ))
            ) : (
              !isSearching &&
              !searchOutletController.isDebouncing &&
              searchOutletController.extrasResults.length > 0 && (
                <div className="col-span-full flex flex-col animate-fadeUp items-center mt-8">
                  <div className="text-center text-gray-400">No issues found</div>
                </div>
              )
            )}
          </div>
          {hasMoreLatest && !isSearching && (
            <div className="flex justify-center mt-4 mb-2">
              <Button
                variant="outlined"
                color="white"
                onClick={() => searchOutletController.loadMoreLatest()}
                disabled={isLoadingMore}
                className="flex items-center gap-2 !border-light-grey/50 !text-white hover:!bg-white/10 transition"
              >
                {isLoadingMore ? <Spinner className="h-4 w-4" /> : null}
                {isLoadingMore ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </section>
        {
          searchOutletController.extrasResults.length > 0 && (
            <section className="mt-4 pb-2">
              <Typography variant="h2" className="text-white text-2xl md:text-3xl mb-2">
                Extras
              </Typography>

              <div
                className="lg:px-2 w-full grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] justify-items-center gap-x-1 gap-y-8 py-2 mt-2"
              >
                {searchOutletController.extrasResults.map((issue) => (
                  <MinifiedIssueCard
                    issue={issue}
                    key={issue.id}
                  />
                ))}
              </div>
            </section>
          )
        }
        {!isSearching && !searchOutletController.isDebouncing &&
          upcomingResults.length === 0 &&
          latestResults.length === 0 &&
          extrasResults.length === 0 &&
          <div className="text-lg opacity-70 mt-10">No results found</div>
        }
      </>
    </div>
  );
});

const MinifiedIssueSkeleton = () => (
  <div className="w-[140px] h-[330px] flex flex-col items-center justify-between animate-pulse">
    <div className="h-[90%] w-full flex flex-col items-start gap-0.5">
      <div className="w-[140px] h-[195px] bg-gray-300/20 flex-shrink-0" />
      <div className="flex flex-col mt-4 gap-2 w-full items-start justify-between flex-auto">
        <div className="flex flex-col items-start gap-1 w-full">
          <div className="w-3/4 h-4 bg-gray-300/20 rounded" />
          <div className="w-full h-3 bg-gray-300/20 rounded" />
          <div className="w-2/3 h-3 bg-gray-300/20 rounded" />
        </div>
        <div className="w-1/2 h-4 bg-gray-300/20 rounded mb-1" />
      </div>
    </div>
    <div className="w-full flex flex-row items-center justify-between pt-1 border-t border-gray-800">
      <div className="w-8 h-8 bg-gray-300/20 rounded" />
      <div className="w-16 h-8 bg-gray-300/20 rounded" />
    </div>
  </div>
);

const MinifiedUpcomingIssueCard: React.FC<{ issue: StockistIssue }> = observer(({ issue }) => {
  const daysUntil = Math.ceil(
    (new Date(issue.issueOnSale).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  const store = storageController.getOrCreateStore(issue);
  const canInc = store.orderedAmount < MAX_ISSUES_AVAILABLE;
  const canDec = store.orderedAmount > 0;
  const navigate = useNavigate();

  const onInc = () =>
    store.onCopiesChange(Math.min(MAX_ISSUES_AVAILABLE, store.orderedAmount + 1));
  const onDec = () =>
    store.onCopiesChange(Math.max(0, store.orderedAmount - 1));

  const onAddToBasketButtonClick = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to add magazines to your basket.",
        "warning",
        4000
      );
      loginSignupController.setLoginSignupWindowVisible(true);
      return;
    }
    store.confirmOrder(issue);
  };

  return (
    <div className="flex justify-center">
      <div className={`flex flex-col items-center justify-between animate-fadeUp w-[140px] h-[330px]`}>
        <div className="h-full w-full flex flex-col items-center sm:items-start gap-0.5">
          <div
            className="w-[140px] h-[195px] flex-shrink-0 mx-auto relative mb-3 cursor-pointer"
            onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); }}
          >
            {issue.imageAvailable ? (
              <img
                src={
                  issue.imageURL_140x195 ||
                  "/images/coming_soon_140x195.jpg"
                }
                alt={`${issue.identity} cover`}
                title={`See this issue of ${issue.magazine.titleDisplay}`}
                className={`w-[140px] h-[195px] object-cover shadow-sm transition-all duration-300 ${issue.issueOnSale <= new Date() && issue.stock.stockAvailableForStockist <= 0
                  ? "grayscale opacity-50"
                  : ""
                  }`}
              />
            ) : (
              <div className="relative w-full h-full flex flex-col items-center justify-center">
                {issue.previousIssuesImageURLs.length > 0 ?
                  <>
                    {(issue.previousIssuesImageURLs.slice(0, 1)).map((url, i) =>
                      <img
                        key={i}
                        src={url}
                        alt={`Previous Issue ${i + 1}`}
                        title={`See this issue of ${issue.magazine.titleDisplay}`}
                        className={`brightness-[80%] grayscale-[20%] w-[140px] h-[195px] absolute object-cover shadow-lg top-0 left-0 z-10 rotate-0 "top-0 left-0`}
                      />
                    )}
                    <span className={`absolute text-white/80 font-bold select-none pointer-events-none z-30 -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 rotate-[-45deg] uppercase text-2xl w-full text-center`}>Sample Cover</span>
                  </> :
                  <div
                    title="No cover available"
                    className="flex flex-col items-center justify-center w-[140px] h-[195px] bg-main outline outline-1 outline-light-grey/50 object-cover shadow-sm transition-all duration-300"
                  >
                    <span className="text-white italic text-3xl font-bold text-center">NEW LAUNCH!</span>
                  </div>
                }
                {issue.previousIssuesImageURLs.length > 0 ?
                  <span className="absolute -bottom-3 right-0 text-[10px] leading-none text-light-grey/50 italic">Past issue cover</span> :
                  <span className="absolute -bottom-3 right-0 text-[10px] leading-none text-light-grey/50 italic">No cover available</span>
                }
              </div>
            )}
            <div
              className={`${daysUntil <= 1 ? "bg-red-500 animate-slowPulseElementRotated" : ""
                } absolute z-[35] bottom-2 right-0 bg-purple text-white text-xs font-semibold px-1 md:px-2 py-1 rounded shadow-[0_0_3px_1px_#aaa] border border-white transition duration-150 rotate-[-15deg] hover:scale-110 hover:shadow-[0_0_5px_2px_#aaa]`}
            >
              {daysUntil} day{daysUntil !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="flex flex-col w-full items-start justify-between gap-2 flex-auto">
            <div className="flex flex-col items-center sm:items-start w-full">
              <div className="text-sm font-bold w-full truncate text-center sm:text-left">
                <a
                  className="transition duration-150 ease-in-out text-white hover:text-gray-400 cursor-pointer"
                  title={`See latest issues of ${issue.magazine.titleDisplay}`}
                  onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(issue.magazine.stockistMagazineUrl); }}
                >
                  {issue.magazine.titleDisplay}
                </a>
              </div>
              <div className="text-gray-500 flex flex-col justify-start w-full">
                <span title={issue.identity} className="truncate text-xs">{issue.identity}</span>
                <span
                  className={`text-xs ${daysUntil <= 1 ? "text-red-500 font-bold" : ""}`}
                  title={issue.onSaleDateDisplay}>{issue.onSaleDateDisplay}
                </span>
              </div>
            </div>
            <div className="flex gap-2 justify-start items-center w-full">
              <div className="text-sm font-semibold">{issue.priceDisplay}</div>
            </div>
          </div>
        </div>

        <div className="pt-1 border-t justify-between border-gray-700 flex w-full gap-1">
          <button
            className="cursor-pointer disabled:cursor-default disabled:opacity-50"
            onClick={onAddToBasketButtonClick}
            disabled={store.orderedAmount <= 0}
          >
            <ShoppingCartIcon
              className="w-8 h-8 hover:text-gray-300 transition duration-150"
              color="white"
            />
          </button>
          <div className="flex items-center">
            <Button
              variant="outlined"
              color="red"
              onClick={onDec}
              disabled={!canDec}
              className="h-8 !py-1 !px-2 md:!px-2 !border-light-grey/50 !text-white text-md hover:!bg-red-500 hover:!border-red-500 transition rounded-r-none"
              aria-label="decrease"
            >
              -
            </Button>

            <input
              type="number"
              name="quantity"
              min={0}
              max={
                issue.issueOnSale > new Date()
                  ? MAX_ISSUES_AVAILABLE
                  : issue.stock.issuesAvailableIncludingBasket
              }
              value={store.orderedAmount}
              onChange={(e) => {
                const parsed = parseInt(e.target.value || "0", 10) || 0;
                store.onCopiesChange(
                  Math.max(0, Math.min(parsed, MAX_ISSUES_AVAILABLE - issue.stock.inBasket))
                );
              }}
              className="w-8 h-8 text-center bg-gray-700 text-white border border-gray-600 focus:outline-none !appearance-none rounded-none"
            />

            <Button
              variant="outlined"
              color="green"
              onClick={onInc}
              disabled={!canInc}
              className="h-8 !py-1 !px-2 md:!px-2 !border-light-grey/50 !text-white text-md hover:!bg-green-500 hover:!border-green-500 transition rounded-l-none"
              aria-label="increase"
            >
              +
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const MinifiedIssueCard: React.FC<{ issue: StockistIssue }> = observer(({ issue }) => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const buttonSize = windowSize < 880 ? 'md' : 'lg';
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const onBuyButtonClick = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to buy magazines.",
        "warning",
        4000
      )
      loginSignupController.setLoginSignupWindowVisible(true)
      return;
    }
    setOrderDialogOpen(true);
  }

  return (
    <div className={`flex flex-col items-center justify-between animate-fadeUp w-[140px] h-[330px]`}>
      <div className="h-[90%] w-full">
        <div className="h-full w-full flex flex-col items-start gap-0.5">
          <div className="d-inline-block mx-auto w-full">
            <img
              src={issue.imageURL_140x195}
              className="mx-auto w-[140px] h-[195px] cursor-pointer"
              alt={`${issue.identity} cover`}
              title={`See this issue of ${issue.magazine.titleDisplay}`}
              onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); }}
            />
          </div>
          <div className="flex flex-col mt-4 gap-2 w-full items-start justify-between flex-auto">
            <div className="flex flex-col items-start w-full">
              <div className="text-sm font-bold w-full truncate text-center xsm:text-left">
                <Link
                  className="transition duration-150 ease-in text-white hover:text-gray-400 cursor-pointer"
                  title={`See latest issues of ${issue.magazine.titleDisplay}`}
                  to={issue.magazine.stockistMagazineUrl}
                  onClick={() => issueDetailsController.setMagazineHint(issue.magazineId)}
                >
                  {issue.magazine.titleDisplay}
                </Link>
              </div>
              <div className="text-gray-500 flex flex-col justify-start w-full">
                <span title={issue.identity} className="text-xs truncate">{issue.identity}</span>
                <span className="text-xs" title={issue.onSaleDateDisplay}>{issue.onSaleDateDisplay}</span>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="text-sm font-semibold">{issue.priceDisplay}</div>
              <span className="whitespace-nowrap text-gray-500 text-xs">
                ({issue.stock.issuesAvailableIncludingBasket > 30 ? "30+" : issue.stock.issuesAvailableIncludingBasket} in stock)
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-auto w-full flex flex-row justify-between gap-4 w-[140px] pt-1 border-t border-gray-700">
        <button
          className="cursor-pointer disabled:cursor-default disabled:opacity-50"
          onClick={() => onBuyButtonClick()}
          disabled={issue.stock.issuesAvailableIncludingBasket <= 0}
        >
          <ShoppingCartIcon
            className="w-8 h-8 hover:text-gray-300 transition duration-150"
            color="white"
          />
        </button>
        <Button
          className="transition duration-300 ease-in-out text-base !p-1 !shadow-none"
          color={"white"}
          variant={"text"}
          size={buttonSize}
          onClick={() => { issueDetailsController.setMagazineHint(issue.magazineId); navigate(`/issue/${issue.id}`); }}
        >
          Details
        </Button>
      </div>

      <Dialog
        issue={issue}
        orderDialogOpen={orderDialogOpen}
        setOrderDialogOpen={setOrderDialogOpen}
      />
    </div>
  );
});